function formatKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Recursively flattens any nested DRF / Django / Axios error structure into human-readable strings.
 */
export function extractErrorStrings(val: any, prefix: string = ""): string[] {
  if (val === null || val === undefined) return [];

  // Plain string
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return [];
    // Skip HTML error pages (e.g. from 500 debug view)
    if (trimmed.startsWith("<") && (trimmed.includes("<!DOCTYPE") || trimmed.includes("<html") || trimmed.includes("<body"))) {
      return [];
    }
    return prefix ? [`${prefix}: ${trimmed}`] : [trimmed];
  }

  // Numbers or booleans
  if (typeof val === "number" || typeof val === "boolean") {
    return prefix ? [`${prefix}: ${val}`] : [String(val)];
  }

  // Arrays (e.g. ["This field is required."], or array of objects: [{ event_name: [...] }])
  if (Array.isArray(val)) {
    const results: string[] = [];
    val.forEach((item, index) => {
      if (item === null || item === undefined) return;
      // If item is empty object, skip (common in DRF list serializer errors)
      if (typeof item === "object" && !Array.isArray(item) && Object.keys(item).length === 0) return;

      const subPrefix = typeof item === "object" && !Array.isArray(item)
        ? (prefix ? `${prefix} #${index + 1}` : `#${index + 1}`)
        : prefix;

      results.push(...extractErrorStrings(item, subPrefix));
    });
    return results;
  }

  // Objects
  if (typeof val === "object") {
    const results: string[] = [];

    // Keys that carry metadata instead of user-facing errors
    const ignoredKeys = new Set(["stack", "code", "config", "request", "response", "isAxiosError"]);

    // If it's a DRF standard detail: e.g. { detail: "Access denied" }
    if (typeof val.detail === "string" && val.detail.trim()) {
      return prefix ? [`${prefix}: ${val.detail.trim()}`] : [val.detail.trim()];
    }

    const entries = Object.entries(val).filter(([k]) => !ignoredKeys.has(k));

    for (const [key, subVal] of entries) {
      if (key === "name" && (subVal === "AxiosError" || subVal === "Error")) continue;
      if (key === "success" && subVal === false) continue;
      if (key === "status" && typeof subVal === "number") continue;
      if (key === "message" && typeof subVal === "string" && subVal.startsWith("Request failed with status code")) continue;
      if (key === "stringify_error" && typeof subVal === "string") continue;

      const isGenericWrapper = ["non_field_errors", "error", "errors", "data"].includes(key);
      const keyLabel = isGenericWrapper ? "" : formatKey(key);
      const nextPrefix = prefix
        ? (keyLabel ? `${prefix} -> ${keyLabel}` : prefix)
        : keyLabel;

      results.push(...extractErrorStrings(subVal, nextPrefix));
    }

    // Fallback to stringify_error if nothing else could be parsed
    if (results.length === 0 && typeof val.stringify_error === "string" && val.stringify_error.trim()) {
      results.push(...extractErrorStrings(val.stringify_error, prefix));
    }

    return results;
  }

  return [];
}

/**
 * Extracts a user-friendly error message from an unknown error object.
 * Guaranteed to return a non-empty string and NEVER return "[object Object]".
 */
export const getApiErrorMessage = (err: any, fallback: string = "Something went wrong. Please try again."): string => {
  if (!err) return fallback;

  // 1. If err is directly a string
  if (typeof err === "string") {
    const trimmed = err.trim();
    if (trimmed && !trimmed.startsWith("<") && trimmed !== "[object Object]") {
      return trimmed;
    }
    return fallback;
  }

  // 2. Extract potential data container
  const data = err?.response?.data ?? err?.data ?? err;

  // 3. Extract strings recursively
  const messages = extractErrorStrings(data);
  if (messages.length > 0) {
    const validMessages = messages.filter((m) => m && !m.includes("[object Object]"));
    if (validMessages.length > 0) {
      // Deduplicate
      const unique = Array.from(new Set(validMessages));
      return unique.join(" | ");
    }
  }

  // 4. Try standard Error.message
  if (err?.message && typeof err.message === "string") {
    const msg = err.message.trim();
    if (msg && !msg.startsWith("Request failed with status code") && msg !== "[object Object]") {
      return msg;
    }
  }

  return fallback;
};
